import { format } from 'date-fns';
import { useMemo } from 'react';
import { StyleSheet } from 'react-native';
import {
  Badge,
  Box,
  HStack,
  MarkdownText,
  Sheet,
  Text,
  VStack,
} from '../../../components/primitives';
import { Button } from '../../../components/primitives/Button';
import { useTranslation } from '../../../contexts/TranslationContext';
import { useResponsive } from '../../../hooks/useResponsive';
import { useTranslateOnFly } from '../../../hooks/useTranslateOnFly';
import { formatCurrency, formatNumber } from '../../../lib/format';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import { useAgentDisplayName } from '../hooks/useAgentDisplayName';
import { useApproveToolAction, useRejectToolAction, useToolActions } from '../hooks/useToolActions';
import type { AgentExecution, AgentExecutionStatus } from '../types';
import { ToolActionFeed } from './ToolActionFeed';

interface ExecutionDetailSheetProps {
  execution: AgentExecution | null;
  isOpen: boolean;
  onClose: () => void;
  onCancel?: (id: string) => void;
}

const STATUS_COLORS: Record<AgentExecutionStatus, 'green' | 'red' | 'amber' | 'neutral' | 'cyan'> =
  {
    completed: 'green',
    failed: 'red',
    running: 'amber',
    queued: 'neutral',
    cancelled: 'neutral',
  };

function formatTimestamp(value: string | null): string {
  if (!value) return '-';
  try {
    return format(new Date(value), 'MMM d, yyyy HH:mm:ss');
  } catch {
    return '-';
  }
}

function formatDuration(ms: number | null): string {
  if (ms === null) return '-';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function formatCost(value: string | null | undefined): string {
  if (value == null) return formatCurrency(0, 'USD', 'en-US');
  const num = Number.parseFloat(value);
  if (Number.isNaN(num)) return formatCurrency(0, 'USD', 'en-US');
  return formatCurrency(num, 'USD', 'en-US');
}

function formatTokenCount(value: number): string {
  return formatNumber(value);
}

function DetailRow({ label, value }: Readonly<{ label: string; value: string }>) {
  return (
    <HStack spacing="sm" align="start" justify="between">
      <Text variant="caption" color={colors.text.tertiary} style={{ flex: 1 }}>
        {label}
      </Text>
      <Text
        variant="caption"
        color={colors.text.primary}
        style={{ fontWeight: '500', flex: 2, textAlign: 'right' }}
      >
        {value}
      </Text>
    </HStack>
  );
}

function SectionTitle({ title }: Readonly<{ title: string }>) {
  return (
    <Text
      variant="caption"
      color={colors.text.secondary}
      style={{ fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.6 }}
    >
      {title}
    </Text>
  );
}

export function ExecutionDetailSheet({
  execution,
  isOpen,
  onClose,
  onCancel,
}: Readonly<ExecutionDetailSheetProps>) {
  const { t } = useTranslation();
  const agentName = useAgentDisplayName();
  const { isMobile } = useResponsive();
  const position = isMobile ? 'bottom' : 'right';

  const rawReasoning = useMemo(() => {
    if (!execution?.output) return null;
    return (execution.output.reasoning ?? execution.output.explanation ?? null) as string | null;
  }, [execution]);

  const { reasoning: translatedReasoning } = useTranslateOnFly({
    reasoning: rawReasoning ?? undefined,
  });

  const reasoning = translatedReasoning ?? rawReasoning;

  const canCancel = execution?.status === 'queued' || execution?.status === 'running';

  const { data: toolActionsData } = useToolActions(execution?.organizationId ?? '', execution?.id);

  const approveAction = useApproveToolAction();
  const rejectAction = useRejectToolAction();
  const toolActions = toolActionsData?.actions ?? [];

  if (!execution) {
    return (
      <Sheet visible={isOpen} onClose={onClose} position={position} size="md">
        <Box padding="xl" alignItems="center" justifyContent="center">
          <Text variant="body" color={colors.text.tertiary}>
            {t('agents.execution.noSelection')}
          </Text>
        </Box>
      </Sheet>
    );
  }

  return (
    <Sheet
      visible={isOpen}
      onClose={onClose}
      title={agentName(execution.agentType)}
      position={position}
      size="md"
    >
      <VStack spacing="lg">
        <HStack spacing="sm" align="center">
          <Badge color={STATUS_COLORS[execution.status]} variant="subtle">
            {execution.status.charAt(0).toUpperCase() + execution.status.slice(1)}
          </Badge>
        </HStack>

        {execution.lead && (
          <VStack spacing="sm">
            <SectionTitle title={t('agents.execution.lead')} />
            <DetailRow label={t('agents.execution.name')} value={execution.lead.fullName} />
            <DetailRow label={t('agents.execution.email')} value={execution.lead.email} />
          </VStack>
        )}

        <VStack spacing="sm">
          <SectionTitle title={t('agents.execution.timing')} />
          <DetailRow
            label={t('agents.execution.started')}
            value={formatTimestamp(execution.startedAt)}
          />
          <DetailRow
            label={t('agents.execution.completed')}
            value={formatTimestamp(execution.completedAt)}
          />
          <DetailRow
            label={t('agents.execution.duration')}
            value={formatDuration(execution.durationMs)}
          />
        </VStack>

        <VStack spacing="sm">
          <SectionTitle title={t('agents.execution.model')} />
          <DetailRow label={t('agents.execution.model')} value={execution.modelUsed ?? '-'} />
          <DetailRow
            label={t('agents.execution.tokens')}
            value={`${formatTokenCount(execution.inputTokens)} in / ${formatTokenCount(execution.outputTokens)} out`}
          />
          <DetailRow label={t('agents.execution.cost')} value={formatCost(execution.costUsd)} />
        </VStack>

        {execution.errorMessage && (
          <VStack spacing="sm">
            <SectionTitle title={t('agents.execution.error')} />
            <Box
              backgroundColor={colors.status.error.bg}
              borderRadius="md"
              borderWidth={1}
              borderColor={colors.status.error.main}
              padding="md"
            >
              <Text variant="caption" color={colors.status.error.light}>
                {execution.errorMessage}
              </Text>
            </Box>
          </VStack>
        )}

        {reasoning && (
          <VStack spacing="sm">
            <SectionTitle title={t('agents.execution.reasoning')} />
            <Box
              backgroundColor={colors.surface.row}
              borderRadius="md"
              borderWidth={1}
              borderColor={colors.border.subtle}
              padding="md"
            >
              <MarkdownText>{reasoning}</MarkdownText>
            </Box>
          </VStack>
        )}

        {toolActions.length > 0 && (
          <VStack spacing="sm">
            <SectionTitle title={t('agents.execution.toolActions')} />
            <ToolActionFeed
              actions={toolActions}
              onApprove={(id) => approveAction.mutate(id)}
              onReject={(id) => rejectAction.mutate({ actionId: id })}
              isApproving={approveAction.isPending}
              isRejecting={rejectAction.isPending}
            />
          </VStack>
        )}

        {canCancel && onCancel && (
          <Button
            variant="outline"
            onPress={() => onCancel(execution.id)}
            textColor={colors.status.error.main}
            style={styles.cancelButton}
          >
            {t('agents.execution.cancelExecution')}
          </Button>
        )}
      </VStack>
    </Sheet>
  );
}

const styles = StyleSheet.create({
  cancelButton: {
    borderColor: colors.status.error.main,
    marginTop: tokens.spacing.sm,
  },
});
